import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  LinearProgress,
  Grid,
  useTheme,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  Videocam as VideoIcon,
} from '@mui/icons-material';
import brand from '../../../config/brand';

interface MediaUploaderProps {
  files: File[];
  onChange: (files: File[]) => void;
  maxFiles?: number;
  maxSizeMB?: number;
  uploading?: boolean;
  uploadProgress?: number;
}

const ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'video/mp4',
  'video/webm',
  'video/quicktime',
];

const MediaUploader: React.FC<MediaUploaderProps> = ({
  files,
  onChange,
  maxFiles = 10,
  maxSizeMB = 50,
  uploading = false,
  uploadProgress = 0,
}) => {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return `${file.name}: Unsupported file type.`;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      return `${file.name}: Exceeds ${maxSizeMB}MB limit.`;
    }
    return null;
  };

  const addFiles = useCallback(
    (newFiles: FileList | File[]) => {
      const arr = Array.from(newFiles);
      const remaining = maxFiles - files.length;
      if (remaining <= 0) return;

      const validFiles: File[] = [];
      arr.slice(0, remaining).forEach((f) => {
        const err = validateFile(f);
        if (!err) validFiles.push(f);
      });

      if (validFiles.length > 0) {
        onChange([...files, ...validFiles]);
      }
    },
    [files, maxFiles, onChange]
  );

  const removeFile = (index: number) => {
    onChange(files.filter((_, i) => i !== index));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Box>
      {/* Drop zone */}
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => inputRef.current?.click()}
        sx={{
          border: '2px dashed',
          borderColor: dragOver
            ? brand.colors.fire
            : theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.15)'
            : 'rgba(0,0,0,0.15)',
          borderRadius: 3,
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          bgcolor: dragOver
            ? `rgba(${brand.colorsRgb.fire}, 0.05)`
            : 'transparent',
          '&:hover': {
            borderColor: brand.colors.fire,
            bgcolor: `rgba(${brand.colorsRgb.fire}, 0.03)`,
          },
        }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED_TYPES.join(',')}
          style={{ display: 'none' }}
          onChange={(e) => e.target.files && addFiles(e.target.files)}
        />
        <UploadIcon
          sx={{ fontSize: 48, color: brand.colors.fire, opacity: 0.7, mb: 1 }}
        />
        <Typography variant="body1" fontWeight={600} gutterBottom>
          Drag & drop files here or click to browse
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Images (JPEG, PNG, GIF, WebP) and Videos (MP4, WebM, MOV)
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
          Max {maxFiles} files, up to {maxSizeMB}MB each
        </Typography>
      </Box>

      {/* Upload progress */}
      {uploading && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{
              borderRadius: 2,
              height: 6,
              '& .MuiLinearProgress-bar': {
                background: brand.gradients.button,
              },
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}
          >
            Uploading... {uploadProgress}%
          </Typography>
        </Box>
      )}

      {/* Preview grid */}
      {files.length > 0 && (
        <Grid container spacing={1.5} sx={{ mt: 2 }}>
          {files.map((file, index) => {
            const isVideo = file.type.startsWith('video/');
            const previewUrl = URL.createObjectURL(file);

            return (
              <Grid item xs={4} sm={3} md={3} key={`${file.name}-${index}`}>
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.1)'
                        : 'rgba(0,0,0,0.1)',
                    aspectRatio: '1',
                  }}
                >
                  {isVideo ? (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.03)'
                            : 'rgba(0,0,0,0.03)',
                      }}
                    >
                      <VideoIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                    </Box>
                  ) : (
                    <Box
                      component="img"
                      src={previewUrl}
                      alt={file.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  )}

                  {/* Remove button */}
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'rgba(0,0,0,0.6)',
                      color: 'white',
                      width: 24,
                      height: 24,
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>

                  {/* File info overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      bgcolor: 'rgba(0,0,0,0.6)',
                      px: 0.5,
                      py: 0.25,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="white"
                      sx={{
                        fontSize: '0.6rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.3,
                      }}
                    >
                      {isVideo ? (
                        <VideoIcon sx={{ fontSize: 10 }} />
                      ) : (
                        <ImageIcon sx={{ fontSize: 10 }} />
                      )}
                      {formatSize(file.size)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      {files.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}
        >
          {files.length}/{maxFiles} files selected
        </Typography>
      )}
    </Box>
  );
};

export default MediaUploader;
